<script lang="ts" setup>
/**
 * リプライ情報。
 */

import { defineProps, inject } from "vue";
import ReplyContents from "@/components/communityTalk/reply/ReplyContents.vue";
import ReplyResponseButton from "@/components/communityTalk/reply/ReplyResponseButton.vue";
import ReplyDate from "@/components/communityTalk/reply/ReplyDate.vue";
import CommunityTalkUser from "@/components/communityTalk/CommunityTalkUser.vue";
import ReplyNice from "@/components/communityTalk/reply/ReplyNice.vue";
import ReplyBestAnswerIcon from "@/components/communityTalk/reply/ReplyBestAnswerIcon.vue";
import ReplyTenantIcon from "@/components/communityTalk/reply/ReplyTenantIcon.vue";
import { ReplyDto } from "@/apiRequests/models/communityTalk/replyGetResponse";
import { CommunityTalkUserViewModel } from "@/viewModels/communityTalk/communityTalkUserViewModel";
import CommunityTalkMedia from "@/components/common/CommunityTalkMedia.vue";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";

interface Props {
	/**
	 * リプライ情報。
	 */
	reply: ReplyDto;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * ReplyPostStoreのInject用キー。
	 */
	replyPostStoreKey: string;
}
const props = defineProps<Props>();

const communityTalkUserViewModel: CommunityTalkUserViewModel = {
	badgeType: props.reply.badgeType,
	nickName: props.reply.nickName,
	profilePicUrl: props.reply.profilePicUrl,
	reviewerAttributeAnswers: props.reply.reviewerAttributeAnswers,
};

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;
</script>

<template>
	<div :class="['revico-community-talk-reply-details', reply.isBestAnswer ? 'revico-community-talk-best-answer' : '', reply.isTenantReply ? 'revico-community-talk-reply-tenant' : '']">
		<div v-if="reply.isTalkRoomOwner" class="revico-community-talk-reply-details-from-talkroom-user">
			<ReplyDate :replyDate="reply.replyDate" />
			<CommunityTalkUser
				:viewModel="communityTalkUserViewModel"
				:post-date="reply.replyDate"
				:new-arrival-badge-display-days="communityTalkStore.getGeneralSettings().newArrivalBadgeDisplayDays"
				:new-arrival-badge-text="communityTalkStore.getWordSettings().newArrivalBadgeText"
			/>
			<ReplyContents :contents="reply.contents" />
			<ReplyResponseButton :nickName="reply.nickName" :contents="reply.contents" :community-talk-store-key="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" />

			<CommunityTalkMedia :images="props.reply.images" :movies="props.reply.movies" />
			<ReplyNice :niceCount="reply.goodCount" :replyId="reply.replyId" :communityTalkStoreKey="communityTalkStoreKey" />
		</div>
		<!-- トークルーム投稿者外のリプライ -->
		<div v-else class="revico-community-talk-reply-details-from-other">
			<ReplyDate :replyDate="reply.replyDate" />
			<CommunityTalkUser
				:viewModel="communityTalkUserViewModel"
				:post-date="reply.replyDate"
				:new-arrival-badge-display-days="communityTalkStore.getGeneralSettings().newArrivalBadgeDisplayDays"
				:new-arrival-badge-text="communityTalkStore.getWordSettings().newArrivalBadgeText"
			/>
			<ReplyContents :contents="reply.contents" />
			<ReplyResponseButton :nickName="reply.nickName" :contents="reply.contents" :community-talk-store-key="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" />

			<CommunityTalkMedia :images="props.reply.images" :movies="props.reply.movies" />
			<ReplyNice :niceCount="reply.goodCount" :replyId="reply.replyId" :communityTalkStoreKey="communityTalkStoreKey" />

			<ReplyBestAnswerIcon v-if="reply.isBestAnswer" :bestAnswerTitle="wordSettings.bestAnswerTitle" />
			<ReplyTenantIcon v-if="reply.isTenantReply" />
		</div>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk {
	// テナント返信である場合、表示領域確保のため上部にマージン付ける
	&-reply-tenant {
		margin-top: 40px;
	}

	.revico-community-talk-reply-details {
		position: relative;
		padding: 20px;
		background-color: var(--reply-bg-color);

		&-from-talkroom-user,
		&-from-other {
			--arrow-size: 20px;
			&::after {
				content: "";
				display: block;
				position: absolute;
				top: calc(50% - var(--arrow-size));
				width: 0;
				height: 0;
				border-top: var(--arrow-size) solid transparent;
				border-bottom: var(--arrow-size) solid transparent;
			}
		}
		&-from-talkroom-user {
			&::after {
				right: -20px;
				border-left: var(--arrow-size) solid var(--reply-bg-color);
			}
		}
		&-from-other {
			&::after {
				left: -20px;
				border-right: var(--arrow-size) solid var(--reply-bg-color);
			}
		}
	}
}
</style>
